/**
 * Zero-value UID emitted by the device when its identifier is not yet available
 * (e.g. before the first successful connection handshake or after a cold boot).
 */
export const UNKNOWN_DEVICE_UID = '00:00:00:00:00:00';

/**
 * Returns true when a device UID should be considered unknown/invalid.
 * Callers must skip device-match validation when either side is unknown.
 */
export function isUnknownDeviceUid(uid?: string | null): boolean {
  return !uid || uid === UNKNOWN_DEVICE_UID;
}

/**
 * Ediphor USB device identifiers
 * These values must match the actual device's USB descriptors
 */
export const EDIPHOR_USB = {
  /** USB Vendor ID for Ediphor devices (pid.codes shared VID, no longer Zephyr's 0x2FE3) */
  VENDOR_ID: 0x1209,

  /** USB Product IDs (for different hardware versions) */
  PRODUCT_IDS: [0x0001],
  
  /** USB interface number for data transfer */
  INTERFACE_NUMBER: 0,
  
  /** Endpoint for sending commands (OUT) */
  ENDPOINT_OUT: 1,
  
  /** Endpoint for receiving responses (IN) */
  ENDPOINT_IN: 1,
  
  /** Maximum packet size for USB transfers */
  MAX_PACKET_SIZE: 512,
  
  /** Maximum message size (for buffering) */
  MAX_MESSAGE_SIZE: 4096,
} as const;

/**
 * Ediphor BLE device identifiers
 *
 * Confirmed against the `Ediphor/dev-console` reference client's GATT contract
 * (see `_bmad-output/specs/spec-ble-connectivity/ble-gatt-contract.md`) — one
 * custom 128-bit service with three characteristics. Do not invent a different
 * UUID scheme; these are the literal values the firmware exposes.
 */
export const EDIPHOR_BLE = {
  /** Custom 128-bit GATT service the EdiphorReader advertises. */
  SERVICE_UUID: '23456469-7068-6f72-0000-58616569616e',

  /** Command write characteristic (app -> device). */
  CHAR_WRITE_UUID: '23456469-7068-6f72-0001-58616569616e',

  /** Response read characteristic (device -> app, via notify). */
  CHAR_READ_UUID: '23456469-7068-6f72-0002-58616569616e',

  /** Error channel characteristic (device -> app, via notify). */
  CHAR_ERROR_UUID: '23456469-7068-6f72-0003-58616569616e',

  /** Advertised device name; "<DEVICE_NAME> <serial>" when disambiguating multiple units. */
  DEVICE_NAME: 'EdiphorReader',

  /**
   * Conservative chunk-size floor used only when the connection's negotiated MTU is
   * unavailable (Web Bluetooth does not expose a standardized, universally-supported way
   * to read it back). Never used as a fixed chunk size when the MTU IS known — see
   * `BleDeviceService`'s chunk-size computation, which mirrors the reference's
   * `min(244, max(20, mtu - 3))` formula.
   */
  DEFAULT_CHUNK_SIZE: 20,

  /**
   * Ceiling applied to the MTU-derived chunk size, used as today's known-good upper bound
   * (observed empirically by the bleak-based reference client). Web Bluetooth's MTU
   * negotiation differs from bleak's OS-level access, so this ceiling is flagged for
   * re-verification against real hardware — not assumed correct forever — but it is the
   * value the chunk-size computation actually clamps to today.
   */
  MAX_CHUNK_SIZE: 244,

  /** ATT protocol header overhead subtracted from a raw negotiated MTU value. */
  ATT_HEADER_OVERHEAD: 3,
} as const;

/**
 * Timing configuration
 */
export const TIMING = {
  /** Timeout for connection attempt (ms) */
  CONNECTION_TIMEOUT: 10000,
  
  /** Timeout for individual command (ms) */
  COMMAND_TIMEOUT: 5000,
  
  /** Interval for state polling during measurement (ms) — kept for legacy reference, no longer used by runMeasurement */
  POLL_INTERVAL: 250,
  
  /** Delay before retry after error (ms) */
  RETRY_DELAY: 1000,
  
  /** Maximum measurement duration before timeout (ms) */
  MAX_MEASUREMENT_DURATION: 300000,

  /**
   * Max silence from device during an active measurement before declaring it stale.
   * If no unsolicited watchdog message arrives within this window, the measurement
   * is aborted with a MEASUREMENT_TIMEOUT error.
   */
  MEASUREMENT_STALE_TIMEOUT: 10000,
  
  /** Debounce for reconnection attempts (ms) */
  RECONNECT_DEBOUNCE: 2000,

  /** Consecutive transferIn failures (at 100ms apart) before giving up on the
   *  endpoint and treating the connection as lost, rather than retrying forever. */
  MAX_CONSECUTIVE_TRANSFER_ERRORS: 10, // ~1s of continuous failure
} as const;

/**
 * Recovery / staleness configuration
 */
export const RECOVERY = {
  /**
   * Maximum age (ms) of a persisted measurementInFlight before it is considered
   * stale and unlikely to be recoverable. The Ediphor measurement cycle is < 5 min,
   * so 10 minutes covers measurement + reconnect with margin.
   */
  STALENESS_THRESHOLD_MS: 10 * 60 * 1000, // 10 minutes

  /**
   * Maximum age (ms) after which a stale measurement is silently discarded on
   * app start without showing any recovery UI.
   */
  AUTO_CLEANUP_THRESHOLD_MS: 24 * 60 * 60 * 1000, // 24 hours
} as const;

/**
 * localStorage keys for device persistence
 */
export const DEVICE_STORAGE = {
  /** Set when a device is successfully connected; cleared on explicit disconnect */
  PAIRED_KEY: 'ediphor-device-paired',
} as const;

/**
 * CRC-mismatch handling for inbound frames.
 */
export const CRC_FALLBACK = {
  /**
   * When true, a received frame whose CRC32 fails but whose protobuf payload still decodes is
   * used as if valid — updating device state and resolving pending commands — instead of being
   * discarded. Every such use is still logged locally and recorded in the harness Communication
   * Log with `crcValid: false` for traceability, regardless of this flag.
   *
   * Set `VITE_ALLOW_CRC_FALLBACK_DECODE=false` to disable without touching receive-loop code,
   * once the underlying signal-integrity issue driving CRC failures is resolved.
   */
  ALLOW_DECODE_ON_MISMATCH: (import.meta.env.VITE_ALLOW_CRC_FALLBACK_DECODE ?? 'true') !== 'false',
} as const;

// Always-visible (not gated by the app's log-level setting) so this build-time config can be
// confirmed from the browser console on any deployed environment without first running
// __setLogLevel('info') — the whole point is a zero-setup way to verify what a given build
// actually baked in for VITE_ALLOW_CRC_FALLBACK_DECODE.
if (typeof window !== 'undefined') {
  console.info(
    `[CRC_FALLBACK] ALLOW_DECODE_ON_MISMATCH = ${CRC_FALLBACK.ALLOW_DECODE_ON_MISMATCH} ` +
      `(VITE_ALLOW_CRC_FALLBACK_DECODE=${import.meta.env.VITE_ALLOW_CRC_FALLBACK_DECODE ?? '<unset, default true>'})`
  );
}

/**
 * Retry configuration
 */
export const RETRY = {
  /** Maximum connection attempts */
  MAX_CONNECTION_RETRIES: 3,
  
  /** Maximum command retries */
  MAX_COMMAND_RETRIES: 2,
  
  /** Exponential backoff base (ms) */
  BACKOFF_BASE: 1000,
  
  /** Maximum backoff delay (ms) */
  BACKOFF_MAX: 10000,
} as const;
